import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCustomersUrl,
  zadProductsUrl,
  zadSaleByIdUrl,
  zadSaleCancelUrl,
  zadSaleDueDateUrl,
  zadSalePromoCodePreviewUrl,
  zadSalesUrl,
  zadSaleUpdateUrl,
  zadSaleValidateAndCollectUrl,
  zadSaleValidateUrl,
  zadStockUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {buildZadSaleListQuery, readZadSalePaymentSummary} from './listQuery'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadSaleWritePayload} from '../zad/saleHelpers'
import {
  extractZadSalePromoCodePreview,
  normalizeZadSalePromoCodeInput,
} from '../zad/salePromotionHelpers'
import {fetchZadStock} from '../zadStock/service'
import {
  CreateZadSaleRequest,
  FetchZadSaleByIdRequest,
  FetchZadSalePickerCustomersRequest,
  FetchZadSalePickerProductsRequest,
  FetchZadSalesRequest,
  PreviewZadSalePromoCodeRequest,
  UpdateZadSaleDueDateRequest,
  UpdateZadSaleRequest,
  ValidateAndCollectZadSaleRequest,
  ValidateZadSaleRequest,
  ZadSaleIdRequest,
} from './types'

export const fetchZadSales = createAsyncThunk(
  'zadSales/fetch',
  async (payload: FetchZadSalesRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadSalesUrl}?${buildZadSaleListQuery(payload)}`)
      const {sales, total, totalPages, paymentSummary} = res.data || {}
      return {
        success: true as const,
        sales: Array.isArray(sales) ? sales : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
        paymentSummary:
          payload.dateMode === 'PAYMENT' ? readZadSalePaymentSummary(paymentSummary) : null,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSaleById = createAsyncThunk(
  'zadSales/fetchById',
  async (payload: FetchZadSaleByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadSaleByIdUrl(payload.id))
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadSale = createAsyncThunk(
  'zadSales/create',
  async (payload: CreateZadSaleRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadSaleWritePayload(payload)
      const res = await axios.post(zadSalesUrl, body)
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadSale = createAsyncThunk(
  'zadSales/update',
  async (payload: UpdateZadSaleRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = sanitizeZadSaleWritePayload(values)
      const res = await axios.post(zadSaleUpdateUrl(id), body)
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const previewZadSalePromoCode = createAsyncThunk(
  'zadSales/previewPromoCode',
  async (payload: PreviewZadSalePromoCodeRequest, {rejectWithValue}) => {
    try {
      const code = normalizeZadSalePromoCodeInput(payload.code)
      const res = await axios.post(zadSalePromoCodePreviewUrl(payload.id), {code})
      const preview = extractZadSalePromoCodePreview(res.data)
      if (!preview) {
        return rejectWithValue({
          success: false as const,
          error: 'Invalid promo code preview',
          errorCode: 'PROMO_CODE_INVALID',
        })
      }
      return {
        success: true as const,
        preview,
        requestedCode: code,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const validateZadSale = createAsyncThunk(
  'zadSales/validate',
  async (payload: ValidateZadSaleRequest, {rejectWithValue, dispatch}) => {
    try {
      const res = payload.dueDate
        ? await axios.post(zadSaleValidateUrl(payload.id), {dueDate: payload.dueDate})
        : await axios.post(zadSaleValidateUrl(payload.id))
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const validateAndCollectZadSale = createAsyncThunk(
  'zadSales/validateAndCollect',
  async (payload: ValidateAndCollectZadSaleRequest, {rejectWithValue, dispatch}) => {
    try {
      const res = await axios.post(zadSaleValidateAndCollectUrl(payload.id), payload.body)
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        sale: res.data.sale,
        payment: res.data.payment,
        reused: Boolean(res.data.reused),
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

/**
 * Payment terms only. The response sale is not decorated with settlement, so
 * callers refetch instead of storing it.
 */
export const updateZadSaleDueDate = createAsyncThunk(
  'zadSales/updateDueDate',
  async (payload: UpdateZadSaleDueDateRequest, {rejectWithValue}) => {
    try {
      const body: {dueDate: string | null; note?: string} = {dueDate: payload.dueDate}
      const note = String(payload.note || '').trim()
      if (note) body.note = note
      const res = await axios.post(zadSaleDueDateUrl(payload.id), body)
      return {
        success: true as const,
        unchanged: Boolean(res.data?.unchanged),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadSale = createAsyncThunk(
  'zadSales/cancel',
  async (payload: ZadSaleIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadSaleCancelUrl(payload.id))
      return {
        success: true as const,
        sale: res.data.sale,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalePickerCustomers = createAsyncThunk(
  'zadSales/fetchPickerCustomers',
  async (payload: FetchZadSalePickerCustomersRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadCustomersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'createdAt',
          sortOrder: 'desc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {customers} = res.data || {}
      return {
        success: true as const,
        customers: Array.isArray(customers) ? customers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalePickerProducts = createAsyncThunk(
  'zadSales/fetchPickerProducts',
  async (payload: FetchZadSalePickerProductsRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadProductsUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 50,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          archiveStatus: 'ACTIVE',
          search: payload?.search || '',
        })}`
      )
      const {products} = res.data || {}
      return {
        success: true as const,
        products: Array.isArray(products) ? products : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalePickerStock = createAsyncThunk(
  'zadSales/fetchPickerStock',
  async (_payload: void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadStockUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })}`
      )
      const {items} = res.data || {}
      return {
        success: true as const,
        items: Array.isArray(items) ? items : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
